import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { createDefaultOffer } from '~/utils/societePayloadBuilder/offersHub/defaults';
import {
  OFFERS_HUB_ENABLED_REGIONS_HELPER_TEXT,
  fieldLabel,
} from '~/utils/societePayloadBuilder/offersHub/fieldLabels';
import {
  formatOfferCardTitle,
  getNextOfferDraftLabel,
  moveOffer,
} from '~/utils/societePayloadBuilder/offersHub/offers';
import { OFFERS_HUB_URGENCY_TYPES } from '~/utils/societePayloadBuilder/offersHub/types';
import type {
  OffersHubOffer,
  OffersHubOfferTag,
  OffersHubUrgencyType,
} from '~/utils/societePayloadBuilder/offersHub/types';

import { PayloadBuilderImageIdField, PayloadBuilderTextField } from '../FormFields';
import PayloadBuilderRegionChipsField from '../RegionChipsField';
import { SectionCard } from '../SectionCard';

import { OffersHubCtaUrlField } from './OffersHubCtaUrlField';

interface OffersHubOffersSectionProps {
  title: string;
  subtitle: string;
  addButtonLabel: string;
  offerTag: OffersHubOfferTag;
  offers: Array<OffersHubOffer>;
  onChange: (offers: Array<OffersHubOffer>) => void;
}

function updateOffer(
  offers: Array<OffersHubOffer>,
  index: number,
  patch: Partial<OffersHubOffer>,
): Array<OffersHubOffer> {
  return offers.map((offer, offerIndex) => (offerIndex === index ? { ...offer, ...patch } : offer));
}

interface OffersHubOfferCardProps {
  offer: OffersHubOffer;
  index: number;
  offers: Array<OffersHubOffer>;
  onChange: (offers: Array<OffersHubOffer>) => void;
}

function OffersHubOfferCard({ offer, index, offers, onChange }: OffersHubOfferCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isPartnerOffer = offer.offerTag === 'partner_offer';
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const title = formatOfferCardTitle(offer);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, expanded ? 0.08 : 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
              }}
            />
          }
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            px: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {title}
          </Typography>
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Chip label={`Position ${index + 1}`} size="small" color="primary" variant="outlined" />
          <Tooltip title="Move offer up">
            <Box component="span">
              <IconButton
                aria-label={`Move offer ${index + 1} up`}
                disabled={index === 0}
                onClick={() => onChange(moveOffer(offers, index, -1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Move offer down">
            <Box component="span">
              <IconButton
                aria-label={`Move offer ${index + 1} down`}
                disabled={index === offers.length - 1}
                onClick={() => onChange(moveOffer(offers, index, 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Button
            size="small"
            variant={offer.isActive ? 'contained' : 'outlined'}
            color={offer.isActive ? 'success' : 'inherit'}
            onClick={() => onChange(updateOffer(offers, index, { isActive: !offer.isActive }))}
          >
            {offer.isActive ? 'Active' : 'Inactive'}
          </Button>
          <Tooltip title="Remove offer">
            <IconButton
              aria-label={`Remove offer ${index + 1}`}
              onClick={() => onChange(offers.filter((_, offerIndex) => offerIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                select
                label={fieldLabel('urgencyType')}
                value={offer.urgencyType}
                onChange={(event) =>
                  onChange(updateOffer(offers, index, { urgencyType: event.target.value as OffersHubUrgencyType }))
                }
                required
              >
                {OFFERS_HUB_URGENCY_TYPES.map((urgencyType) => (
                  <MenuItem key={urgencyType} value={urgencyType}>
                    {urgencyType}
                  </MenuItem>
                ))}
              </PayloadBuilderTextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderRegionChipsField
                label={fieldLabel('enabledRegions')}
                helperText={OFFERS_HUB_ENABLED_REGIONS_HELPER_TEXT}
                regions={offer.enabledRegions}
                onChange={(enabledRegions) => onChange(updateOffer(offers, index, { enabledRegions }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('startAt')}
                type="datetime-local"
                value={offer.startAt}
                onChange={(event) => onChange(updateOffer(offers, index, { startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('endAt')}
                type="datetime-local"
                value={offer.endAt}
                onChange={(event) => onChange(updateOffer(offers, index, { endAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            {isPartnerOffer ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label={fieldLabel('brandName')}
                    value={offer.brandName}
                    onChange={(event) => onChange(updateOffer(offers, index, { brandName: event.target.value }))}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label={fieldLabel('industry')}
                    value={offer.industry}
                    onChange={(event) => onChange(updateOffer(offers, index, { industry: event.target.value }))}
                    required
                  />
                </Grid>
              </>
            ) : null}
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderImageIdField
                label={fieldLabel('imageId')}
                required
                value={offer.imageId}
                onChange={(imageId) => onChange(updateOffer(offers, index, { imageId }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label={fieldLabel('title')}
                value={offer.title}
                onChange={(event) => onChange(updateOffer(offers, index, { title: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label={fieldLabel('description')}
                value={offer.description}
                onChange={(event) => onChange(updateOffer(offers, index, { description: event.target.value }))}
                required
                multiline
                minRows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label={fieldLabel('ctaLabel')}
                value={offer.ctaLabel}
                onChange={(event) => onChange(updateOffer(offers, index, { ctaLabel: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <OffersHubCtaUrlField
                value={offer.ctaUrl}
                onChange={(event) => onChange(updateOffer(offers, index, { ctaUrl: event.target.value }))}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function OffersHubOffersSection({
  title,
  subtitle,
  addButtonLabel,
  offerTag,
  offers,
  onChange,
}: OffersHubOffersSectionProps) {
  return (
    <SectionCard title={title} subtitle={subtitle} count={offers.length}>
      <Stack spacing={2}>
        {offers.map((offer, index) => (
          <OffersHubOfferCard key={offer.draftId} offer={offer} index={index} offers={offers} onChange={onChange} />
        ))}
        <Button
          variant="outlined"
          onClick={() => onChange([...offers, createDefaultOffer(offerTag, getNextOfferDraftLabel(offers))])}
        >
          {addButtonLabel}
        </Button>
      </Stack>
    </SectionCard>
  );
}
